export type ReportsTimeRange = 'today' | 'yesterday' | 'week' | '12w' | '12m' | 'custom';

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Local calendar YYYY-MM-DD (not UTC), for `<input type="date">`. */
export function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getRangeBounds(
  range: ReportsTimeRange,
  customStart: string,
  customEnd: string,
  now: Date = new Date()
): { start: Date; end: Date } {
  const end = new Date(now);
  const today = startOfDay(end);
  switch (range) {
    case 'today': {
      const dayEnd = new Date(today);
      dayEnd.setHours(23, 59, 59, 999);
      return { start: today, end: dayEnd };
    }
    case 'yesterday': {
      const start = addDays(today, -1);
      const dayEnd = new Date(start);
      dayEnd.setHours(23, 59, 59, 999);
      return { start, end: dayEnd };
    }
    case 'week':
      return { start: addDays(today, -6), end };
    case '12w':
      return { start: addDays(today, -7 * 12 + 1), end };
    case '12m':
      return { start: addDays(today, -365 + 1), end };
    case 'custom': {
      if (!customStart || !customEnd) return { start: addDays(today, -6), end };
      const start = new Date(`${customStart}T00:00:00`);
      const customEndDate = new Date(`${customEnd}T23:59:59.999`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(customEndDate.getTime())) {
        return { start: addDays(today, -6), end };
      }
      if (start.getTime() > customEndDate.getTime()) {
        return { start: customEndDate, end: start };
      }
      return { start, end: customEndDate };
    }
    default:
      return { start: addDays(today, -6), end };
  }
}

export function isCompletedOrderStatus(status?: string | null): boolean {
  return String(status || '').toLowerCase().trim() === 'completed';
}

export function isCreatedToday(createdAt?: string | null, now: Date = new Date()): boolean {
  const { start, end } = getRangeBounds('today', '', '', now);
  const t = new Date(createdAt || '').getTime();
  return Number.isFinite(t) && t >= start.getTime() && t <= end.getTime();
}

export function ordersInRange<T extends { created_at?: string | null }>(
  orders: T[],
  start: Date,
  end: Date
): T[] {
  const startMs = start.getTime();
  const endMs = end.getTime();
  return orders.filter((order) => {
    const t = new Date(order.created_at || '').getTime();
    return Number.isFinite(t) && t >= startMs && t <= endMs;
  });
}
